import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { path } from "./therapyPaths";
import TherapyNav from "./TherapyNav";
import TherapyFooter from "./TherapyFooter";
import { Icon } from "./Icons";
import TherapistPortrait from "./TherapistPortrait";
import { THERAPISTS } from "./therapists";
import "./Therapy.css";
import "./Therapists.css";

function MiniTherapistCard({ therapist }) {
    return (
        <Link
            to={path(`therapists/${therapist.slug}`)}
            className="therapist-mini-card"
        >
            <div className="therapist-mini-portrait-svg">
                <TherapistPortrait therapist={therapist} size="mini" />
            </div>
            <div className="therapist-mini-info">
                <h4>{therapist.name}</h4>
                <p>{therapist.specializations.slice(0, 2).join(" · ")}</p>
            </div>
        </Link>
    );
}

function TherapyHome() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const featured = THERAPISTS.slice(0, 4);

    return (
        <main className="demo demo-therapy">
            <TherapyNav />

            <section className="demo-hero">
                <div className="demo-container demo-hero-grid">
                    <div className="demo-hero-content">
                        <p className="demo-eyebrow">A group practice in Brooklyn Heights</p>
                        <h1>Therapy that meets you where you actually are.</h1>
                        <p className="demo-subhead">
                            Eight licensed clinicians offering individual and couples
                            therapy for adults navigating anxiety, burnout,
                            relationships, trauma, and life transitions. Practical,
                            warm, and grounded in evidence-based approaches.
                        </p>

                        <div className="demo-actions">
                            <Link to={path("therapists")} className="btn-primary">
                                Browse therapists
                            </Link>
                            <Link to={path("booking")} className="btn-secondary">
                                Free consultation
                            </Link>
                        </div>

                        <div className="trust-row">
                            <div className="trust-item">
                                <Icon name="shield" />
                                <span>Licensed in NY &amp; NJ</span>
                            </div>
                            <div className="trust-item">
                                <Icon name="user" />
                                <span>8 specialized clinicians</span>
                            </div>
                            <div className="trust-item">
                                <Icon name="video" />
                                <span>In-person &amp; telehealth</span>
                            </div>
                        </div>
                    </div>

                    <div className="demo-hero-visual">
                        <img
                            src="/images/therapists/office.jpg"
                            alt=""
                            className="hero-image"
                        />
                        <div className="hero-accent-card">
                            <div className="hero-accent-dot" />
                            <div className="hero-accent-text">
                                <span className="hero-accent-label">Now accepting</span>
                                <span className="hero-accent-value">New clients · Spring 2026</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="demo-section">
                <div className="demo-container">
                    <div className="section-header">
                        <p className="demo-eyebrow">Our approach</p>
                        <h2>Therapy is a relationship, not a script.</h2>
                        <p className="section-lede">
                            Our clinicians draw from CBT, EMDR, IFS, Gottman,
                            mindfulness-based work and more — but the work always
                            starts with what you're bringing into the room. No two
                            clients need the same thing, and we don't pretend
                            otherwise.
                        </p>
                    </div>

                    <div className="demo-grid-3 approach-grid">
                        <div className="approach-card">
                            <Icon name="compass" className="approach-icon" />
                            <h3>Practical and direct</h3>
                            <p>
                                You'll leave sessions with concrete things to think
                                about and try, not vague encouragement. We name
                                what's going on and work on it.
                            </p>
                        </div>

                        <div className="approach-card">
                            <Icon name="heart" className="approach-icon" />
                            <h3>Warm without performing</h3>
                            <p>
                                Empathy matters, but so does honesty. We'll push back
                                when it's useful, sit with you when it's not, and
                                won't pretend to have answers we don't.
                            </p>
                        </div>

                        <div className="approach-card">
                            <Icon name="layers" className="approach-icon" />
                            <h3>Match before momentum</h3>
                            <p>
                                The right therapist makes the work possible. We help
                                you find the right one — even if that means
                                referring you out.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="demo-section demo-alt">
                <div className="demo-container">
                    <div className="section-header">
                        <p className="demo-eyebrow">Our clinicians</p>
                        <h2>Eight specialists, one practice.</h2>
                        <p className="section-lede">
                            Each of our therapists has specific areas they work in.
                            Browse the full roster, filter by what you're looking for,
                            or start with a free consultation if you're not sure
                            where to begin.
                        </p>
                    </div>

                    <div className="therapist-mini-grid">
                        {featured.map((t) => (
                            <MiniTherapistCard key={t.slug} therapist={t} />
                        ))}
                    </div>

                    <div className="demo-actions demo-actions-center" style={{ marginTop: "2rem" }}>
                        <Link to={path("therapists")} className="btn-primary">
                            See all 8 therapists
                        </Link>
                    </div>
                </div>
            </section>

            <section className="demo-section">
                <div className="demo-container">
                    <div className="section-header">
                        <p className="demo-eyebrow">What to expect</p>
                        <h2>How it works.</h2>
                    </div>

                    <ol className="demo-steps">
                        <li>
                            <strong>Find a therapist or start with a consult</strong>
                            <p>
                                Browse the roster filtered by what you're looking
                                for, or schedule a free 20-minute consultation if
                                you'd rather talk first.
                            </p>
                        </li>
                        <li>
                            <strong>Book your first session</strong>
                            <p>
                                Pick a time that works on the therapist's calendar.
                                Payment for paid sessions happens at booking.
                            </p>
                        </li>
                        <li>
                            <strong>Pre-session questionnaire</strong>
                            <p>
                                A short questionnaire tailored to your therapist's
                                practice. Helps them prepare; doesn't take long.
                            </p>
                        </li>
                        <li>
                            <strong>Secure clinical intake</strong>
                            <p>
                                Within 24 hours, you'll get a separate invitation
                                to our HIPAA-compliant client portal where you'll
                                complete the full clinical intake.
                            </p>
                        </li>
                        <li>
                            <strong>Your first session</strong>
                            <p>
                                In person at the Brooklyn office or via secure
                                telehealth. You can ask anything you want about how
                                your therapist works.
                            </p>
                        </li>
                    </ol>
                </div>
            </section>

            <section className="demo-section demo-alt">
                <div className="demo-container">
                    <div className="info-strip">
                        <div className="info-strip-item">
                            <h4>Insurance</h4>
                            <p>
                                We're out-of-network. We provide monthly superbills
                                for reimbursement through your insurance.
                            </p>
                        </div>
                        <div className="info-strip-item">
                            <h4>Sliding scale</h4>
                            <p>
                                Most clinicians hold a small number of reduced-fee
                                slots for clients experiencing financial hardship.
                            </p>
                        </div>
                        <div className="info-strip-item">
                            <h4>Sessions</h4>
                            <p>
                                In-person at Brooklyn Heights or telehealth across
                                NY and NJ. Most clients mix both.
                            </p>
                        </div>
                        <div className="info-strip-item">
                            <h4>Questions?</h4>
                            <p>
                                <Link to={path("faq")}>Read the FAQ</Link> or{" "}
                                <Link to={path("booking")}>schedule a consultation</Link>{" "}
                                to ask in person.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="demo-section demo-cta">
                <div className="demo-container">
                    <h2>Ready when you are.</h2>
                    <p>
                        The hardest part is usually the first call. It's free,
                        there's no pressure, and we'll be honest about whether we're
                        the right practice for you.
                    </p>
                    <div className="demo-actions demo-actions-center">
                        <Link to={path("therapists")} className="btn-primary">
                            Find your therapist
                        </Link>
                        <Link to={path("booking")} className="btn-secondary">
                            Free consultation
                        </Link>
                    </div>
                </div>
            </section>

            <TherapyFooter />
        </main>
    );
}

export default TherapyHome;